import { useState } from 'react';
import { Plus, Pencil, Trash2, DoorOpen, LayoutDashboard, DraftingCompass } from 'lucide-react';
import type { Room } from '@peles/shared';
import { EntityList, StatusBadge } from '../common';
import type { EntityListColumn } from '../common';
import { useRooms, useDeleteRoom } from '../../hooks/useFloorPlans';
import { RoomFormModal } from '../floor-plan/RoomFormModal';
import { RoomShapeEditorModal } from '../floor-plan/room-shape/RoomShapeEditorModal';

interface Props {
  venueId: string;
}

export function VenueRoomListView({ venueId }: Props) {
  const { data: rooms = [], isLoading } = useRooms(venueId);
  const deleteRoom = useDeleteRoom();

  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [shapingRoom, setShapingRoom] = useState<Room | null>(null);

  function openCreate() {
    setEditingRoom(null);
    setShowModal(true);
  }

  function openEdit(room: Room) {
    setEditingRoom(room);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRoom(null);
  }

  /** A new room goes straight on to its shape and fixtures: they are part of defining it. */
  function handleSaved(room: Room, created: boolean) {
    closeModal();
    if (created) setShapingRoom(room);
  }

  async function handleDelete(id: string) {
    try {
      await deleteRoom.mutateAsync(id);
    } catch {
      // deletion failure is non-blocking; room list will refetch
    } finally {
      setDeletingId(null);
    }
  }

  const columns: EntityListColumn<Room>[] = [
    {
      key: 'name',
      header: 'Room',
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => (
        <div className="flex items-center gap-2">
          <LayoutDashboard size={14} className="text-gray-400 flex-shrink-0" />
          <span className="font-medium text-gray-900">{r.name}</span>
        </div>
      ),
    },
    {
      key: 'roomType',
      header: 'Type',
      hideOnMobile: true,
      render: (r) =>
        r.roomType ? (
          <StatusBadge label={r.roomType} color="blue" />
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'dimensions',
      header: 'Size (ft)',
      hideOnMobile: true,
      render: (r) => (
        <span className="text-sm text-gray-600 whitespace-nowrap">
          {r.widthFt} × {r.heightFt}
          {r.shape && <span className="ml-1 text-xs text-primary-600">· drawn</span>}
        </span>
      ),
    },
    {
      key: 'maxCapacity',
      header: 'Banquet',
      hideOnMobile: true,
      sortable: true,
      // A room with none sorts last either way, as the list does with any gap.
      sortValue: (r) => r.maxCapacity,
      render: (r) => (
        <span className="text-sm text-gray-600">{r.maxCapacity != null ? r.maxCapacity : '—'}</span>
      ),
    },
    {
      key: 'cocktailCapacity',
      header: 'Cocktail',
      hideOnMobile: true,
      sortable: true,
      sortValue: (r) => r.cocktailCapacity,
      render: (r) => (
        <span className="text-sm text-gray-600">
          {r.cocktailCapacity != null ? r.cocktailCapacity : '—'}
        </span>
      ),
    },
    {
      key: 'entries',
      header: 'Entries',
      hideOnMobile: true,
      render: (r) =>
        r.entries.length > 0 ? (
          <span className="flex items-center gap-1 text-sm text-gray-500">
            <DoorOpen size={13} />
            {r.entries.length}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-28',
      align: 'right',
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEdit(r);
            }}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            title="Edit room"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShapingRoom(r);
            }}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            title="Shape & fixtures"
            aria-label={`Shape and fixtures of ${r.name}`}
          >
            <DraftingCompass size={14} />
          </button>
          {deletingId === r.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(r.id);
                }}
                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingId(null);
                }}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeletingId(r.id);
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete room"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <EntityList<Room>
        title="Rooms"
        items={rooms}
        columns={columns}
        getKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No rooms yet. Add a room to get started."
        emptyIcon={<LayoutDashboard size={32} className="text-gray-300" />}
        titleActions={
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={15} />
            Add Room
          </button>
        }
      />

      <RoomFormModal
        open={showModal}
        onClose={closeModal}
        room={editingRoom}
        defaultVenueId={venueId}
        venueLocked
        onSaved={handleSaved}
      />
      {shapingRoom && (
        <RoomShapeEditorModal open onClose={() => setShapingRoom(null)} room={shapingRoom} />
      )}
    </>
  );
}
